import { Platform, Share } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';

import { markUnexpectedFailureHandled } from '../../shared/sentry/presentUnexpectedFailure';
import { reportUserVisibleFailure } from '../../shared/sentry/sentryUser';

export type CsrShareOutcome = 'ok' | 'cancelled';

function sanitizeCsrFileName(raw: string): string {
  const clean = raw.trim().replace(/[^\w.-]+/g, '-').replace(/-+/g, '-');
  return clean.endsWith('.csr') ? clean : `${clean}.csr`;
}

/**
 * Comparte/descarga un archivo .csr (PEM de texto plano, sin BOM).
 * Mismo patrón que shareOrDownloadCsv pero con MIME PKCS#10.
 */
/** Reporta a Sentry (`user_visible`) sin toast: los callers ya muestran error. */
function reportShareFailureAndThrow(error: unknown, userMessage: string): never {
  try {
    reportUserVisibleFailure({
      module: 'fiscal',
      op: 'csr_share_or_download',
      error,
      title: 'Descargar CSR',
      detail: userMessage,
    });
  } catch {
    // Reporting must never replace the share failure path.
  }
  throw markUnexpectedFailureHandled(new Error(userMessage));
}

export async function shareOrDownloadCsr(csrPem: string, rawFilename: string): Promise<CsrShareOutcome> {
  const filename = sanitizeCsrFileName(rawFilename);

  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    try {
      const blob = new Blob([csrPem], { type: 'application/pkcs10' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);
      return 'ok';
    } catch (e) {
      reportShareFailureAndThrow(e, 'No se pudo iniciar la descarga en el navegador.');
    }
  }

  try {
    const uri = `${FileSystem.cacheDirectory}${filename}`;
    await FileSystem.writeAsStringAsync(uri, csrPem, {
      encoding: FileSystem.EncodingType.UTF8,
    });
    const canShare = await Sharing.isAvailableAsync();
    if (!canShare) {
      const result = await Share.share({ title: filename, message: csrPem });
      if (result.action === Share.dismissedAction) return 'cancelled';
      return 'ok';
    }
    await Sharing.shareAsync(uri, { mimeType: 'application/pkcs10', dialogTitle: filename });
    return 'ok';
  } catch (e) {
    if (e instanceof Error && /cancel/i.test(e.message)) return 'cancelled';
    reportShareFailureAndThrow(e, 'No se pudo compartir el archivo CSR.');
  }
}
